import { useSyncExternalStore } from "react";

/**
 * Builder theme store. The initial value comes from the pre-paint bootstrap
 * script in public/index.html (`?theme=` handover param from the platform,
 * persisted to localStorage, applied as a `dark` class on <html> before first
 * paint). The in-builder toggle updates the same class + storage, so the next
 * platform handover param — when present — still wins on entry.
 */
export type Theme = "light" | "dark";

const listeners = new Set<() => void>();
let transitionTimer: number | undefined;

export const getActiveTheme = (): Theme => (document.documentElement.classList.contains("dark") ? "dark" : "light");

export const setTheme = (theme: Theme): void => {
  const root = document.documentElement;
  // Arm the cross-fade only for the duration of the switch (see the
  // .theme-transition rules in shadcn-tokens.css), then disarm so transitions
  // never affect normal hover/focus interactions. Clearing the previous timer
  // keeps a rapid re-toggle from cutting its own fade short.
  window.clearTimeout(transitionTimer);
  root.classList.add("theme-transition");
  root.classList.toggle("dark", theme === "dark");
  transitionTimer = window.setTimeout(() => root.classList.remove("theme-transition"), 350);
  try {
    localStorage.setItem("theme", theme);
  } catch {
    /* storage unavailable — class still applies for this session */
  }
  listeners.forEach((l) => l());
};

/**
 * Dark mode is scoped to the redesigned builder: legacy pages in this SPA are
 * not dark-themed. The builder mount applies the persisted theme (covers SPA
 * navigation INTO the builder, where the index.html bootstrap didn't rerun)
 * and removes the class on unmount so navigation away lands on light pages.
 */
export const applyStoredTheme = (): void => {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem("theme");
  } catch {
    /* storage unavailable — keep current class */
  }
  if (stored === "dark" || stored === "light") {
    document.documentElement.classList.toggle("dark", stored === "dark");
    listeners.forEach((l) => l());
  }
};

export const clearThemeClass = (): void => {
  document.documentElement.classList.remove("dark", "theme-transition");
  window.clearTimeout(transitionTimer);
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Reactive theme value — re-renders consumers when the toggle flips it. */
export const useTheme = (): Theme => useSyncExternalStore(subscribe, getActiveTheme);
